/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Logger } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { CurrentExamContextService } from '@app/common/current-exam-context.service';
import { UtilService } from '@app/util/util.service';
import { MessageService } from '@core/lib/services/message.service';
import { Subject } from 'rxjs';

const log = new Logger('ToggleKnobService');

@Injectable({
  providedIn: 'root'
})
export class ToggleKnobService {
  private fullScreenMode = false;
  private workloadManagerIsOpen = true;

  preference: string;
  pinnedWorklistBarSize;
  workloadManagerSize;
  diagnosticHubSize;
  listContainerSize;

  private screenLayoutObserver$: Subject<boolean> = new Subject<boolean>();
  private workloadManagerOpenObserver$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private userPreferenceService: UserPreferenceService,
    private currentExamContextService: CurrentExamContextService,
    private messageService: MessageService,
    private utilService: UtilService
  ) {}

  initialize() {
    this.preference = this.userPreferenceService.getUserPreferenceFromCache('screen-mode');
    if (this.preference !== undefined) {
      if (this.preference === 'split-screen') {
        this.deactivateFullScreenMode();
      } else if (this.preference === 'full-screen') {
        this.activateFullScreenMode();
      } else if (this.preference === 'four-column') {
        // below code is temporary to fix persisted four-column preference in environments and should be removed
        this.preference = 'split-screen';
        this.deactivateFullScreenMode();
        this.userPreferenceService.createOrUpdateUserPreference('screen-mode', 'split-screen');
      }
    } else {
      this.preference = 'split-screen';
      this.deactivateFullScreenMode();
    }
  }

  getScreenLayoutObserver() {
    return this.screenLayoutObserver$;
  }
  getWorkloadManagerOpenObserver() {
    return this.workloadManagerOpenObserver$;
  }

  updateWorkloadManagerState() {
    log.debug('updating workloadManagerSize and diagnosticHubSize');
    this.workloadManagerOpenObserver$.next(this.workloadManagerIsOpen);
  }

  getLayoutMode = (): string => {
    return this.fullScreenMode ? 'full' : 'small';
  };

  isFullScreenMode = () => {
    return this.fullScreenMode;
  };

  activateFullScreenMode() {
    log.debug('activating full screen mode');
    this.fullScreenMode = true;
    this.updateWorkloadManagerState();
    this.screenLayoutObserver$.next(this.fullScreenMode);
  }

  deactivateFullScreenMode() {
    log.debug('activating split screen mode');
    this.fullScreenMode = false;
    this.workloadManagerIsOpen = true;
    this.updateWorkloadManagerState();
    this.comparisonFlyout(true);
    this.screenLayoutObserver$.next(this.fullScreenMode);
  }

  activateFullScreenModeAndUpdateUserPreference() {
    this.activateFullScreenMode();
    this.userPreferenceService.createOrUpdateUserPreference('screen-mode', 'full-screen');
  }

  deactivateFullScreenModeAndUpdateUserPreference() {
    this.deactivateFullScreenMode();
    this.userPreferenceService.createOrUpdateUserPreference('screen-mode', 'split-screen');
  }

  openWorkloadManagerInFullScreenMode() {
    log.debug('opening workload manager in full screen mode');
    this.fullScreenMode = true;
    this.openWorkloadManager();
  }

  isWorkloadManagerOpen() {
    return this.workloadManagerIsOpen;
  }

  openWorkloadManager() {
    log.debug('opening workload manager');
    this.workloadManagerIsOpen = true;
    if (this.fullScreenMode) {
      this.comparisonFlyout(false);
    }
    this.messageService.broadcast('workloadManagerOpened');
    this.updateWorkloadManagerState();
  }

  openDiagnosticHub = () => {
    log.debug('opening diagnostic hub');
    if (this.fullScreenMode) {
      this.workloadManagerIsOpen = false;
      this.updateWorkloadManagerState();
    }
  };

  comparisonFlyout(flag) {
    const tab = this.utilService.getActiveTab();
    if (tab) {
      if (flag) {
        if (tab.comparisons && tab.comparisons.differentPatient && tab.comparisons.differentPatient.length > 0) {
          tab.isComparisonPopoverOpen = true;
        }
      } else {
        tab.isComparisonPopoverOpen = false;
      }
    }
  }
}
